import type { IAreaCobertura, IGrupoCobertura, IMicroCobertura } from '../api';
import { indexarBots } from './derivarCobertura';

/**
 * "Quantas empresas NÃO CHEGARAM a esta etapa" — o pedido do dono (07/10):
 *
 * > *"tem 100 empresas iniciais no agrupamento 1, porém no agrupamento 2
 * > rodaram apenas 90 empresas, eu quero que no agrupamento 2 tenha esse aviso
 * > informando as 10 empresas que não processaram e o porquê não processaram
 * > aqui. Qual a reclamação de hoje: temos 100 empresas iniciais no primeiro
 * > agrupamento, só que quando eu chego no último agrupamento rodaram apenas
 * > 40-60 empresas, e eu não faço ideia o que aconteceu com o restante"*
 *
 * ────────────────────────────────────────────────────────────────────────────
 * A RÉGUA: "existe log dela para este agrupamento?"
 *
 * `entraram.empresas` é exatamente isso — quem tem registro no bot/grupo. Quem
 * está no universo da área e NÃO está ali simplesmente não chegou: o trabalho
 * não aconteceu, e é esse sumiço que a tela precisava mostrar.
 *
 * MACRO — a base é o UNIVERSO DA ÁREA (escolha do dono): cada agrupamento diz
 * quantas das N empresas da área ainda não chegaram nele. Não compara com o nó
 * anterior: a pergunta é "quanto falta do total", não "quanto perdi na etapa".
 *
 * MICRO — a base é o BOT ANTERIOR, e só quando há dependência cadastrada
 * (`dep_bot`). Sem dependência não há "etapa anterior" e o indicador não
 * aparece — foi o pedido explícito: *"comparando o bot com bot somente se eles
 * estiverem interligados com dependência"*.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * O QUE ESTE MÓDULO NÃO FAZ MAIS (e por quê)
 *
 * A primeira versão (07/10, revertida no mesmo dia) comparava os bots DENTRO do
 * nó entre si — "falhou em algum bot do agrupamento". Respondia outra pergunta:
 * o dono quer saber quem não chegou ao agrupamento, não quem tropeçou dentro
 * dele. No micro aquilo ainda produzia "nenhum bot deste nó a processou" para
 * um nó que É um bot só, e inflava o número acima do que a macro mostrava.
 */

/** Por que a empresa não está nesta etapa. */
export type MotivoNaoChegou =
  /** Tem log aqui, mas o trabalho não concluiu. */
  | 'nao_concluiu'
  /** Não tem log nenhum nesta etapa: não chegou. */
  | 'sem_log';

export interface EmpresaNaoChegou {
  codiEmp: string;
  motivo: MotivoNaoChegou;
  /**
   * ONDE ela parou (07/10, 2ª rodada do pedido):
   *
   * > *"o único motivo aparentemente é que a empresa não tem log... mas deve
   * > ter outro motivo como erro e sem conseguir prosseguir"*
   *
   * "Não tem log aqui" é o SINTOMA; a causa é ter travado num nó anterior. O
   * nome do nó sai do próprio `/cobertura` — cada grupo publica quem entrou e
   * quem concluiu nele, e a comparação diz onde a empresa ficou.
   *
   * `null` quando ela não aparece em nó nenhum do fluxo: aí a resposta honesta
   * é "nunca entrou no fluxo", e inventar um nó seria pior que não saber.
   */
  parouEm: string | null;
}

/** Quantas empresas pararam em cada nó — o resumo que abre a seção. */
export interface ParadaPorNo {
  /** Nome do nó onde travaram, ou `null` para "nunca entraram no fluxo". */
  no: string | null;
  quantidade: number;
}

export interface NaoChegaram {
  /** A base da comparação: universo da área (macro) ou o bot anterior (micro). */
  base: number;
  /** Rótulo da base, para a tela dizer sobre o quê o número é. */
  baseLabel: string;
  /** Quem tem log nesta etapa. */
  chegaram: number;
  /** Quem está na base e não chegou aqui, ou chegou e não concluiu. */
  itens: EmpresaNaoChegou[];
  semLog: EmpresaNaoChegou[];
  naoConcluiram: EmpresaNaoChegou[];
  /**
   * O resumo que abre a seção: quantas pararam em cada nó, do maior para o
   * menor. É por aqui que se age — "68 pararam em Auditor Impostos SN" aponta
   * um nó para investigar, enquanto 90 linhas de código de empresa não apontam
   * nada.
   */
  porNo: ParadaPorNo[];
  /** Alguma lista veio truncada do servidor — a tela avisa em vez de mentir. */
  truncado: boolean;
}

const ordenar = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { numeric: true });

/**
 * ONDE a empresa parou — a causa por trás do "não tem log aqui" (07/10).
 *
 * Varre os nós do fluxo e devolve, para cada empresa, o ÚLTIMO em que ela
 * aparece sem ter concluído: é ali que o trabalho dela travou. Se concluiu em
 * todos os que a tocaram, devolve o último que a tocou — ela chegou até ali e
 * o fluxo parou de levá-la adiante, o que também é uma resposta.
 *
 * `null` para quem não aparece em nó nenhum: "nunca entrou no fluxo". Inventar
 * um nó aí seria pior que admitir que não se sabe.
 *
 * ORDEM: usa `nos` na ordem em que vem — o BFF já lineariza a cadeia de
 * dependência por BFS em `_montar_linhas_grupos`, e é essa ordem que a tela
 * desenha. Reordenar aqui faria o diagnóstico discordar do desenho.
 */
const ondeParou = (nos: NoDoFluxo[]): Map<string, string | null> => {
  /** Onde ela TRAVOU (entrou e não concluiu) — a resposta preferida. */
  const travou = new Map<string, string | null>();
  /** O último nó que a tocou — a resposta de reserva. */
  const ultimoQueTocou = new Map<string, string | null>();

  nos.forEach(no => {
    const concluiu = new Set(no.concluiram);
    no.entraram.forEach(codi => {
      // `no.nome` pode ser `null` (cadastro sem nome): ele entra no mapa assim
      // mesmo, e o `??` do fecho o descarta — "parou em Sem nome" seria um nó
      // inventado, e a resposta honesta é "nunca entrou no fluxo".
      //
      // Uma guarda `if (!no.nome) return` aqui era REDUNDANTE: medido com os
      // dois caminhos, o `porNo` sai idêntico, porque o `??` já faz o trabalho.
      ultimoQueTocou.set(codi, no.nome);
      // Varrendo na ordem do fluxo, o ÚLTIMO em que ela travou é o que fica:
      // é o ponto mais avançado a que ela chegou sem conseguir sair.
      if (!concluiu.has(codi)) travou.set(codi, no.nome);
    });
  });

  const mapa = new Map<string, string | null>();
  ultimoQueTocou.forEach((ultimo, codi) => mapa.set(codi, travou.get(codi) ?? ultimo ?? null));
  return mapa;
};

/**
 * Um nó do fluxo, reduzido ao que o diagnóstico precisa.
 *
 * `nome: null` = cadastro sem nome publicado. O nó continua valendo para o
 * CÁLCULO (a empresa passou por ele), mas não pode ser EXIBIDO como destino:
 * "parou em Sem nome" seria um nó inventado, e ninguém consegue agir sobre ele.
 * Essas empresas caem em "nunca entrou no fluxo", que é a resposta honesta.
 */
interface NoDoFluxo {
  nome: string | null;
  entraram: string[];
  concluiram: string[];
}

/** Agrupa por nó, do maior para o menor — o resumo que abre a seção. */
const resumirPorNo = (itens: EmpresaNaoChegou[]): ParadaPorNo[] => {
  const contagem = new Map<string | null, number>();
  itens.forEach(e => contagem.set(e.parouEm, (contagem.get(e.parouEm) ?? 0) + 1));
  return (
    Array.from(contagem.entries())
      .map(([no, quantidade]) => ({ no, quantidade }))
      // Maior primeiro (é o nó a investigar); "nunca entrou" por último, porque
      // não é um nó e não dá para agir sobre ele da mesma forma.
      .sort((a, b) => {
        // `null` ("nunca entrou") sempre DEPOIS de um nó real: ele não é um nó
        // para investigar, e deixá-lo no topo por ser o maior mandaria o leitor
        // para o único lugar onde não há o que fazer. Os dois ramos são
        // consistentes entre si — `a` nulo manda `a` para o fim (positivo), `b`
        // nulo mantém `a` na frente (negativo).
        if (a.no === null) return 1;
        if (b.no === null) return -1;
        return b.quantidade - a.quantidade || a.no.localeCompare(b.no, 'pt-BR');
      })
  );
};

const montar = (
  base: string[],
  comLog: Set<string>,
  concluidas: Set<string>,
  baseLabel: string,
  truncado: boolean,
  fluxo: NoDoFluxo[],
): NaoChegaram => {
  const parada = ondeParou(fluxo);
  const semLog: EmpresaNaoChegou[] = [];
  const naoConcluiram: EmpresaNaoChegou[] = [];
  base.forEach(codi => {
    const parouEm = parada.get(codi) ?? null;
    if (!comLog.has(codi)) semLog.push({ codiEmp: codi, motivo: 'sem_log', parouEm });
    else if (!concluidas.has(codi)) {
      naoConcluiram.push({ codiEmp: codi, motivo: 'nao_concluiu', parouEm });
    }
  });
  // A LISTA É SÓ DE QUEM NÃO RODOU — `naoConcluiram` fica FORA (09/10).
  //
  // Regra do dono (08/10): *"vamos mostrar apenas as que não processaram ali...
  // pra não confundir mais as coisas e contar a mesma empresa em dois lugares"*.
  //
  // `naoConcluiram` é quem ENTROU e não concluiu — ela rodou, e o ✕ já a conta.
  // Listá-la aqui diz "não rodou neste agrupamento" de uma empresa que tem log
  // de falha no próprio nó, e ainda imprime "parou em X" dentro do popup do
  // próprio X. Foi a auditoria do dono que pegou: a empresa 1480 aparecia na
  // seção E logo abaixo com "Falha (3)" do mesmo bot.
  //
  // Até o mon#92 isto estava latente — `entraram` e `cobertura` chegavam
  // idênticos, então `naoConcluiram` era ~0 e a mistura não aparecia. Com a
  // rota distinguindo, viraram 89 no Auditor Notas Fiscais LP, TODAS já
  // contadas no ✕ 147 (medido: 89 de 89 têm erro ou alerta no `/resumo`).
  //
  // `naoConcluiram` continua publicado no objeto: ele é a resposta de "entrou e
  // travou" e tem uso próprio — só não é item desta lista.
  const itens = semLog;
  return {
    base: base.length,
    baseLabel,
    chegaram: base.filter(c => comLog.has(c)).length,
    itens,
    semLog,
    naoConcluiram,
    porNo: resumirPorNo(itens),
    truncado,
  };
};

/**
 * MACRO — quantas empresas da ÁREA ainda não chegaram a este agrupamento.
 *
 * `universo_empresas` não viaja até o front (o BFF o remove por RBAC), então a
 * base é reconstruída da união dos agrupamentos da área: toda empresa que
 * apareceu em ALGUM nó do fluxo. É o universo observável pela tela, e é
 * honesto — quem nunca apareceu em lugar nenhum não tem como ser listada aqui.
 */
export const naoChegaramAoGrupo = (
  grupo: IGrupoCobertura | undefined,
  area: IAreaCobertura | undefined,
): NaoChegaram | null => {
  if (!grupo || !area) return null;

  const universo = new Set<string>();
  // O UNIVERSO NOMEADO vem do payload quando existe (09/10, bff).
  //
  // Antes a base era só a união dos agrupamentos, e quem está no departamento
  // sem aparecer em nó nenhum ficava invisível: a seção dizia "2 não rodaram"
  // e não tinha código para mostrar. Era o achado do dono — número sem nome
  // não se audita. Medido: Fiscal LP 346 no universo contra 344 na união;
  // Fluxo Municipal 334 sem nome; Contábil 692 de 692.
  //
  // A lista já chega recortada pela carteira, então nomear daqui não vaza
  // empresa de fora — o recorte é do BFF, não desta função.
  (area.universo_empresas ?? []).forEach(c => universo.add(c));
  // A união dos agrupamentos SOMA à lista, não substitui: sem o campo (BFF
  // antigo) ela é a única base, e com ele cobre quem entrou num nó mas não
  // consta do universo publicado — o recorte das duas rotas pode divergir, e
  // sumir com uma empresa que tem log seria pior que listá-la.
  let truncado = false;
  (area.grupos ?? []).forEach(g => {
    (g.entraram?.empresas ?? []).forEach(c => universo.add(c));
    if (g.entraram?.truncada) truncado = true;
  });
  if (!universo.size) return null;

  // O fluxo são os PRÓPRIOS agrupamentos da área, na ordem em que vêm (o BFF já
  // linearizou a cadeia de dependência). É o que permite dizer "parou em
  // Auditor Impostos SN" em vez de só "não tem log aqui".
  const fluxo: NoDoFluxo[] = (area.grupos ?? []).map(g => ({
    nome: g.nome ?? null,
    entraram: g.entraram?.empresas ?? [],
    concluiram: g.cobertura?.empresas ?? [],
  }));

  return montar(
    Array.from(universo).sort(ordenar),
    new Set(grupo.entraram?.empresas ?? []),
    new Set(grupo.cobertura?.empresas ?? []),
    'empresas do departamento',
    truncado,
    fluxo,
  );
};

/**
 * MICRO — quantas empresas que passaram pelo BOT ANTERIOR não chegaram a este.
 *
 * `null` sem `dep_bot`: sem dependência cadastrada não existe "etapa anterior"
 * e qualquer número seria inventado. Também `null` quando a cobertura de um dos
 * dois bots não existe — a pergunta fica sem resposta, e omitir é melhor que
 * exibir zero (que afirmaria "ninguém ficou pelo caminho").
 */
export const naoChegaramAoBot = (
  bot: string,
  depBot: string | null | undefined,
  area: IAreaCobertura | undefined,
): NaoChegaram | null => {
  if (!bot || !depBot || !area) return null;
  const porBot = indexarBots(area);
  const atual = porBot.get(bot);
  const anterior: IMicroCobertura | undefined = porBot.get(depBot);
  if (!atual || !anterior) return null;

  // A base é quem o bot anterior CONCLUIU: quem não concluiu lá ainda está
  // parada nele, e cobrar a etapa seguinte por isso contaria o mesmo problema
  // duas vezes, em dois nós.
  const base = Array.from(new Set(anterior.cobertura?.empresas ?? [])).sort(ordenar);
  if (!base.length) return null;

  // No micro o fluxo são os BOTS da área: o "parou em" nomeia um bot, que é a
  // granularidade deste nível.
  const fluxo: NoDoFluxo[] = (area.micro ?? []).map(m => ({
    nome: m.bot,
    entraram: m.entraram?.empresas ?? [],
    concluiram: m.cobertura?.empresas ?? [],
  }));

  return montar(
    base,
    new Set(atual.entraram?.empresas ?? []),
    new Set(atual.cobertura?.empresas ?? []),
    `concluíram em ${depBot}`,
    Boolean(anterior.cobertura?.truncada) ||
      Boolean(atual.entraram?.truncada) ||
      Boolean(atual.cobertura?.truncada),
    fluxo,
  );
};
